import React, { useEffect, useMemo, useState } from 'react';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import "../../css/admindetails.css";
// Table.jsx lives at src/components/Table.jsx
import { Table } from '../../components/Table';
import { useToast } from '../../contexts/ToastContext';
import { Search, UserPlus, X } from 'lucide-react';

// Matches ADMIN_TIER_ROLES in adminController.js — keep these in sync if
// you add/remove a role on the backend.
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin / Manager' },
  { value: 'sales', label: 'Sales' },
  { value: 'staff', label: 'Staff' },
  { value: 'reporting_manager', label: 'Reporting Manager' },
  { value: 'superadmin', label: 'Super Admin' }
];

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));

// Added phone — optional (User model has it as a plain optional String,
// and existing admins like Manoj/Balaji don't have one on file), so it's
// not required for account creation the way name/email/password are.
const EMPTY_FORM = { name: '', email: '', password: '', role: 'admin', phone: '' };

const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();
  const { addToast, confirm } = useToast();
  const isSuperAdmin = Boolean(currentUser?.isSuperAdmin);

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const showError = (err, fallback) => addToast(err.response?.data?.message || fallback, 'error');

  const load = async (search = '') => {
    try {
      const { data } = await api.get('/admin/users', { params: { search } });
      setUsers(data);
    } catch (err) {
      showError(err, 'Failed to load users.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(searchTerm);
  };

  const toggleAdmin = async (u) => {
    try {
      await api.put(`/admin/users/${u._id}`, { isAdmin: !u.isAdmin });
      load();
    } catch (err) {
      showError(err, 'Failed to update admin access.');
    }
  };

  const handleDelete = async (u) => {
    if (!isSuperAdmin) return; // extra client-side guard; server must enforce this too
    const ok = await confirm({
      title: 'Delete this user?',
      message: `${u.name || u.email} will lose admin access.`,
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u._id}`);
      load();
    } catch (err) {
      showError(err, 'Failed to delete user.');
    }
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormError('');
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setFormError('Name, email, and password are all required.');
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/users', {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        phone: form.phone.trim() || undefined
      });
      setShowAddModal(false);
      setForm(EMPTY_FORM);
      load(searchTerm);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create admin.');
    } finally {
      setSaving(false);
    }
  };

  // Same filter as before — only admins are shown on this page
  const admins = useMemo(() => users.filter((u) => u.isAdmin), [users]);

  const columns = useMemo(
    () => [
      { Header: 'Name', accessor: 'name' },
      { Header: 'Email', accessor: 'email' },
      {
        Header: 'Phone',
        id: 'phone',
        accessor: (u) => u.phone || '-'
      },
      {
        Header: 'Role',
        id: 'role',
        accessor: (u) => ROLE_LABELS[u.role] || (u.isSuperAdmin ? 'Super Admin' : 'Admin'),
        Cell: ({ row }) => {
          const u = row.original;
          const label = ROLE_LABELS[u.role] || (u.isSuperAdmin ? 'Super Admin' : 'Admin');
          return (
            <span className={`aup-role-badge ${u.isSuperAdmin ? 'aup-role-super' : 'aup-role-admin'}`}>
              {label}
            </span>
          );
        }
      },
      {
        Header: '',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => {
          const u = row.original;
          const canDeleteThis = isSuperAdmin && !u.isSuperAdmin; // super admins aren't deletable from here either
          // Same rules as updateUserRole on the server
          const isSelf = String(u._id) === String(currentUser?._id);
          const canRemoveThis = isSuperAdmin && !u.isSuperAdmin && !isSelf;
          let removeHint;
          if (!isSuperAdmin) removeHint = 'Only super admins can change admin access';
          else if (u.isSuperAdmin) removeHint = 'Super admin accounts cannot be changed here';
          else if (isSelf) removeHint = 'You cannot change your own role';
          return (
            <div className="aup-actions">
              <button
                className="aup-btn-warning"
                onClick={() => toggleAdmin(u)}
                disabled={!canRemoveThis}
                title={removeHint}
              >
                Remove Admin
              </button>
              <button
                className="aup-btn-danger"
                onClick={() => handleDelete(u)}
                disabled={!canDeleteThis}
                title={!isSuperAdmin ? 'Only super admins can delete admins' : undefined}
              >
                Delete
              </button>
            </div>
          );
        }
      }
    ],
    [isSuperAdmin, currentUser?._id]
  );


  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Admin Users" />
        <div className="aup-page">
          <div className="aup-header">
            <div className="aup-header-text">
              <h1>Users</h1>
              <p className="aup-subtitle">Staff and administrator accounts with access to this panel.</p>
            </div>
            {isSuperAdmin && (
              <button className="aup-btn-add" onClick={() => setShowAddModal(true)}>
                <UserPlus size={15} />
                Add Admin
              </button>
            )}
          </div>

          <form onSubmit={handleSearch} className="aup-search-form">
            <div className="aup-search-field">
              <Search size={15} className="aup-search-icon" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="aup-search-input"
              />
            </div>
            <button type="submit" className="aup-search-btn">Search</button>
          </form>

          <Table columns={columns} data={admins} show />
        </div>

        {showAddModal && (
          <div className="aup-modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="aup-modal" onClick={(e) => e.stopPropagation()}>
              <header className="aup-modal-head">
                <h2>Add New Admin</h2>
                <button
                  type="button"
                  className="aup-modal-close"
                  aria-label="Close"
                  onClick={() => setShowAddModal(false)}
                >
                  <X size={18} />
                </button>
              </header>
              <form onSubmit={handleAddAdmin}>
                <div className="aup-modal-body">
                  <div className="aup-form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="aup-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      required
                    />
                  </div>
                  <div className="aup-form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      placeholder="Optional"
                      value={form.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                    />
                  </div>
                  <div className="aup-form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      required
                    />
                  </div>
                  <div className="aup-form-group">
                    <label>Role</label>
                    <select
                      value={form.role}
                      onChange={(e) => handleFormChange('role', e.target.value)}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>

                  {formError && <div className="aup-form-error">{formError}</div>}
                </div>

                <div className="aup-modal-actions">
                  <button
                    type="button"
                    className="aup-btn-cancel"
                    onClick={() => {
                      setShowAddModal(false);
                      setForm(EMPTY_FORM);
                      setFormError('');
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="aup-btn-add" disabled={saving}>
                    {saving ? 'Creating...' : 'Create Admin'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
